import { Select, Switch, Tooltip } from "antd";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaQuestionCircle } from "react-icons/fa";
import {
  FiBriefcase,
  FiFileText,
  FiGlobe,
  FiImage,
  FiTruck,
  FiUser
} from "react-icons/fi";
import { IoCloseSharp } from "react-icons/io5";
import { PhoneInput } from "react-international-phone";
import ReactQuill from "react-quill";
import {
  Button,
  Card,
  CardBody,
  Col,
  Label,
  Modal,
  ModalBody,
  Row,
  Spinner,
} from "reactstrap";
import { getDocByKeyValue } from "../../firebase/utility";
import FormInput from "../formInput/FormInput";

const AddNewManufacturer = ({
  modal,
  toggle,
  handleSubmitData,
  selectedObj,
  loading,
}) => {
  const [file, setFile] = useState();
  const [logoFile, setLogoFile] = useState();
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    salesRep: "",
    map: false,
    logo: "",
    websiteLink: "",
    partnerLevel: "",
    display: false,
    notes: "",
    distributors: [],
    visible: true,
    showPriceToGuests: false,
  });

  const handleSubmitManufacturer = () => {
    if (!data?.name || !data?.email) {
      toast.error("Provide Mandatory Fields");
      return;
    }
    if (!data?.logo && !logoFile) {
      toast.error("Please provide a Brand Logo (URL or File Upload)");
      return;
    }
    getDocByKeyValue("Manufacturers", "email", data?.email).then((res) => {
      if (res?.length > 0 && !selectedObj) {
        toast.error("Manufacturer with this email already exists");
        return;
      } else {
        handleSubmitData(data, file, setData, logoFile);
      }
    });
  };

  useEffect(() => {
    if (selectedObj && selectedObj?._id) {
      setFetching(true);
      getDocByKeyValue("Manufacturers", "_id", selectedObj?._id)
        .then((res) => {
          if (res && res.length > 0) {
            const fullData = res[0];
            setData({
              ...fullData,
              name: fullData?.name ?? "",
              address: fullData?.address ?? "",
              phone: fullData?.phone ?? "",
              email: fullData?.email ?? "",
              salesRep: fullData?.salesRep ?? "",
              map: fullData?.map ?? false,
              logo: fullData?.logo ?? "",
              websiteLink: fullData?.websiteLink ?? "",
              partnerLevel: fullData?.partnerLevel ?? "",
              display: fullData?.display ?? false,
              notes: fullData?.notes ?? "",
              distributors: fullData?.distributors ?? [],
              _id: fullData?._id ?? "",
              visible: fullData?.visible ?? true,
              showPriceToGuests: fullData?.showPriceToGuests ?? false,
            });
            setFile(fullData?.loaLos ?? "");
          } else {
            setData({
              ...selectedObj,
              name: selectedObj?.name ?? "",
              address: selectedObj?.address ?? "",
              phone: selectedObj?.phone ?? "",
              email: selectedObj?.email ?? "",
              salesRep: selectedObj?.salesRep ?? "",
              map: selectedObj?.map ?? false,
              logo: selectedObj?.logo ?? "",
              websiteLink: selectedObj?.websiteLink ?? "",
              partnerLevel: selectedObj?.partnerLevel ?? "",
              display: selectedObj?.display ?? false,
              notes: selectedObj?.notes ?? "",
              distributors: selectedObj?.distributors ?? [],
              _id: selectedObj?._id ?? "",
              visible: selectedObj?.visible ?? true,
              showPriceToGuests: selectedObj?.showPriceToGuests ?? false,
            });
            setFile(selectedObj?.loaLos ?? "");
          }
        })
        .catch((err) => {
          console.error("Error fetching manufacturer details:", err);
          toast.error("Failed to load full manufacturer details");
        })
        .finally(() => {
          setFetching(false);
        });
    } else {
      setData({
        name: "",
        address: "",
        phone: "",
        email: "",
        salesRep: "",
        map: false,
        logo: "",
        websiteLink: "",
        partnerLevel: "",
        display: false,
        notes: "",
        distributors: [],
        visible: true,
        showPriceToGuests: false,
      });
      setFile("");
      setLogoFile("");
      setFetching(false);
    }
  }, [selectedObj, modal]);

  const SectionHeader = ({ icon: Icon, title }) => (
    <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
      <div className="bg-primary-soft p-2 rounded-3 text-primary">
        <Icon size={16} />
      </div>
      <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider" style={{ fontSize: '13px' }}>{title}</h6>
    </div>
  );

  return (
    <Modal isOpen={modal} toggle={toggle} centered size="lg" className="manufacturer-modal">
      <ModalBody className="p-0 overflow-hidden rounded-3 border-0">
        {/* Modern Header */}
        <div className="bg-primary-soft p-4 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-white p-2 rounded-circle shadow-sm">
              <FiBriefcase size={24} className="text-primary" />
            </div>
            <div>
              <h4 className="fw-bold text-dark mb-0">
                {selectedObj ? "Update Manufacturer" : "Add New Manufacturer"}
              </h4>
              <p className="text-muted small mb-0">Enter details to {selectedObj ? "update the existing" : "create a new"} manufacturer profile.</p>
            </div>
          </div>
          <div className="bg-white p-1 rounded-circle shadow-sm pointer hover-scale" onClick={toggle}>
            <IoCloseSharp size={24} className="text-muted" />
          </div>
        </div>

        <div className="p-4 bg-light-gray">
          {fetching ? (
            <div className="d-flex flex-column justify-content-center align-items-center py-5">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="mt-3 text-muted fw-bold">Fetching latest details...</p>
            </div>
          ) : (
            <Row className="gy-4">
              {/* Left Column: Basic & Contact */}
              <Col md={12}>
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <SectionHeader icon={FiUser} title="Basic Information" />
                    <Row className="gx-3">
                      <Col md={6}>
                        <FormInput
                          type="text"
                          label="Full Name *"
                          placeholder="e.g. Dell Technologies"
                          value={data?.name}
                          handleChange={(e) => setData({ ...data, name: e.target.value })}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          type="email"
                          label="Business Email *"
                          placeholder="e.g. contact@dell.com"
                          value={data?.email}
                          handleChange={(e) => setData({ ...data, email: e.target.value })}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="fw-bold mb-1">Phone Number</Label>
                        <PhoneInput
                          value={data?.phone}
                          placeholder="Enter phone number"
                          className="w-100 mb-3"
                          inputStyle={{ width: '100%', height: '38px', borderRadius: '8px' }}
                          onChange={(phone) => setData({ ...data, phone })}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          type="text"
                          label="Street Address"
                          placeholder="123 Business Way, City, State"
                          value={data?.address}
                          handleChange={(e) => setData({ ...data, address: e.target.value })}
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              {/* Right Column: Digital & Logistics */}
              <Col md={12}>
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <SectionHeader icon={FiGlobe} title="Contact & Branding" />
                    <Row className="gx-3">
                      <Col md={6}>
                        <FormInput
                          type="text"
                          label="Logo URL"
                          placeholder="https://..."
                          value={data?.logo}
                          handleChange={(e) => setData({ ...data, logo: e.target.value })}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="fw-bold mb-1 d-block">Or Upload Logo</Label>
                        <div className="d-flex align-items-center bg-white rounded-3 border overflow-hidden mb-3" style={{ height: "38px" }}>
                          <input
                            type="file"
                            accept="image/*"
                            className="form-control border-0 shadow-none h-100 py-2 bg-transparent"
                            onChange={(e) => setLogoFile(e.target.files[0])}
                          />
                          <div className="px-2 d-flex align-items-center border-start h-100 bg-light">
                            {(logoFile || data?.logo) ? (
                              <div className="bg-white p-1 rounded border shadow-sm" style={{ height: '30px', width: '30px', flexShrink: 0 }}>
                                {logoFile ? (
                                  <img src={URL.createObjectURL(logoFile)} className="w-100 h-100 object-fit-contain" alt="Logo Preview" />
                                ) : (
                                  <img src={data?.logo} className="w-100 h-100 object-fit-contain" alt="Logo" />
                                )}
                              </div>
                            ) : (
                              <div className="bg-white rounded border d-flex align-items-center justify-content-center text-muted shadow-sm" style={{ height: '30px', width: '30px', flexShrink: 0 }}>
                                <FiImage size={14} />
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <FormInput
                          type="text"
                          label="Website Link"
                          placeholder="https://manufacturer.com"
                          value={data?.websiteLink}
                          handleChange={(e) => setData({ ...data, websiteLink: e.target.value })}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          type="text"
                          label="Account Representative"
                          placeholder="Name of your contact..."
                          value={data?.salesRep}
                          handleChange={(e) => setData({ ...data, salesRep: e.target.value })}
                        />
                      </Col>
                      <Col md={6}>
                        <FormInput
                          type="text"
                          label="Partner Level"
                          placeholder="Gold / Silver / Authorized..."
                          value={data?.partnerLevel}
                          handleChange={(e) => setData({ ...data, partnerLevel: e.target.value })}
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              {/* Partners & Logistics */}
              <Col md={12}>
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <SectionHeader icon={FiTruck} title="Partners & Logistics" />
                    <Row className="gx-3">
                      <Col md={6}>
                        <Label className="fw-bold mb-1">Approved Distributors</Label>
                        <Select
                          mode="tags"
                          allowClear
                          style={{ width: "100%" }}
                          placeholder="Type and press enter (e.g. Ingram Micro, DNH)"
                          className="modern-select shadow-none mb-3"
                          value={data?.distributors}
                          onChange={(val) => setData({ ...data, distributors: val })}
                          options={[]}
                        />
                      </Col>
                      <Col md={6}>
                        <Label className="fw-bold mb-1 d-block">Certifications (LOA/LOS)</Label>
                        <div className="d-flex align-items-center bg-white rounded-3 border overflow-hidden mb-3" style={{ height: "38px" }}>
                          <input
                            type="file"
                            className="form-control border-0 shadow-none h-100 py-1 bg-transparent"
                            onChange={(e) => setFile(e.target.files[0])}
                          />
                          {file && typeof file !== "object" && (
                            <div className="px-2 d-flex align-items-center border-start h-100 bg-light">
                              <a href={file} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary bg-white px-3 fw-bold">
                                View
                              </a>
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="d-flex flex-column gap-2 mb-3">
                          <Label className="fw-bold mb-1 d-flex align-items-center gap-1">
                            Protected MAP <Tooltip color="black" overlayInnerStyle={{ color: 'white' }} title="Enforce Minimum Advertised Price (MAP)"><FaQuestionCircle style={{ color: '#FFD700', cursor: 'pointer', border: '1px solid black', borderRadius: '50%', backgroundColor: 'black' }} /></Tooltip>
                          </Label>
                          <div className="d-flex align-items-center gap-2">
                            <Switch
                              checked={data?.map}
                              onChange={(val) => setData({ ...data, map: val })}
                            />
                            <span className="small fw-600">{data?.map ? "Active" : "Disabled"}</span>
                          </div>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="d-flex flex-column gap-2 mb-3">
                          <Label className="fw-bold mb-1 d-flex align-items-center gap-1">
                            Public Display <Tooltip color="black" overlayInnerStyle={{ color: 'white' }} title="Controls whether the manufacturer's products and brand page are visible to anonymous guest visitors. When set to Hidden, these products can only be browsed by logged-in users."><FaQuestionCircle style={{ color: '#FFD700', cursor: 'pointer', border: '1px solid black', borderRadius: '50%', backgroundColor: 'black' }} /></Tooltip>
                          </Label>
                          <div className="d-flex align-items-center gap-2">
                            <Switch
                              checked={data?.display}
                              onChange={(val) => setData({ ...data, display: val })}
                            />
                            <span className="small fw-600">{data?.display ? "Visible" : "Hidden"}</span>
                          </div>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="d-flex flex-column gap-2 mb-3">
                          <Label className="fw-bold mb-1 d-flex align-items-center gap-1">
                            Visible <Tooltip color="black" overlayInnerStyle={{ color: 'white' }} title="Controls the global visibility of the manufacturer and all its products in the store. When set to No, the manufacturer and its catalog are completely deactivated and hidden from both guests and logged-in users."><FaQuestionCircle style={{ color: '#FFD700', cursor: 'pointer', border: '1px solid black', borderRadius: '50%', backgroundColor: 'black' }} /></Tooltip>
                          </Label>
                          <div className="d-flex align-items-center gap-2">
                            <Switch
                              checked={data?.visible}
                              onChange={(val) => setData({ ...data, visible: val })}
                            />
                            <span className="small fw-600">{data?.visible ? "Yes" : "No"}</span>
                          </div>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="d-flex flex-column gap-2 mb-3">
                          <Label className="fw-bold mb-1 d-flex align-items-center gap-1">
                            Guest Pricing <Tooltip color="black" overlayInnerStyle={{ color: 'white' }} title="Controls whether pricing details and stock quantities are shown to anonymous guest visitors. When set to Hidden, visitors must log in to view the prices and stock status of this manufacturer's products."><FaQuestionCircle style={{ color: '#FFD700', cursor: 'pointer', border: '1px solid black', borderRadius: '50%', backgroundColor: 'black' }} /></Tooltip>
                          </Label>
                          <div className="d-flex align-items-center gap-2">
                            <Switch
                              checked={data?.showPriceToGuests}
                              onChange={(val) => setData({ ...data, showPriceToGuests: val })}
                            />
                            <span className="small fw-600">{data?.showPriceToGuests ? "Visible" : "Hidden"}</span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col md={12}>
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <SectionHeader icon={FiFileText} title="Additional Notes" />
                    <div className="modern-quill rounded-3 overflow-hidden border">
                      <ReactQuill
                        theme="snow"
                        placeholder="Internal notes about this manufacturer..."
                        value={data?.notes}
                        onChange={(notes) => setData({ ...data, notes })}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Footer Actions */}
          <div className="d-flex justify-content-end gap-3 mt-4 pb-2">
            <Button
              color="link"
              className="text-muted text-decoration-none fw-bold px-4"
              onClick={toggle}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              className="px-5 shadow-sm rounded-pill fw-bold"
              disabled={loading || fetching}
              onClick={handleSubmitManufacturer}
            >
              {loading ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner size="sm" />
                  <span>Saving...</span>
                </div>
              ) : (
                selectedObj ? "Update Manufacturer" : "Create Manufacturer"
              )}
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AddNewManufacturer;

